import {ResizeObserver as ResizeObserverPolyfill} from '@juggle/resize-observer';
import useResizeObserver from '@react-hook/resize-observer';
import {RefCallback, useCallback, useLayoutEffect, useState} from 'react';

interface OnResize<T extends HTMLElement> {
  ref: RefCallback<T>;
}

export function useOnResize<T extends HTMLElement = HTMLElement>(onResize: (resized: T) => void): OnResize<T> {
  const [target, setTarget] = useState<T | null>(null);

  const ref = useCallback<(node: T | null) => void>((node) => {
    if (node !== null) {
      setTarget(node);
    }
  }, []);

  useLayoutEffect(() => {
    if (target != null) {
      onResize(target);
    }
  }, [onResize, target]);

  /* v8 ignore start -- @preserve */
  // Cannot test screen resize in jsdom environment

  // https://github.com/jaredLunde/react-hook/issues/308
  // In unit tests (jsdom) or other environments where ResizeObserver is not available, we need to use a polyfill:
  const polyfill =
    typeof window !== 'undefined' && 'ResizeObserver' in window
      ? undefined // No polyfill needed if ResizeObserver API is supported by browser
      : ResizeObserverPolyfill;

  useResizeObserver(target, (entry) => onResize(entry.target as T), {polyfill});
  /* v8 ignore stop */

  return {ref};
}
